import React from 'react';
import { observer } from 'mobx-react-lite';
import { Icon, Label, Message, Segment, Statistic, StatisticGroup } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { TeamStatsDto } from '../../app/models/Team';

interface Props {
  stats?: TeamStatsDto;
}

const resultColor = (r: string) => {
  if (r === 'W') return '#21ba45';
  if (r === 'L') return '#db2828';
  return '#fbbd08';
};

export default observer(function TeamStatsPanel({ stats }: Props) {
  const { t } = useTranslation();

  if (!stats || stats.totalPlayed === 0) {
    return (
      <Segment>
        <Message info>
          <Icon name="info circle" />
          {t('teams.stats.noStats')}
        </Message>
      </Segment>
    );
  }

  const winRate = stats.totalPlayed > 0
    ? Math.round((stats.wins / stats.totalPlayed) * 100)
    : 0;

  const streakColor = stats.streakType === 'Win' ? 'green' : stats.streakType === 'Loss' ? 'red' : 'yellow';

  return (
    <Segment>
      {/* Main stats grid */}
      <StatisticGroup widths={4} size="small" style={{ marginBottom: 20 }}>
        <Statistic>
          <Statistic.Value>{stats.totalPlayed}</Statistic.Value>
          <Statistic.Label>{t('teams.stats.totalPlayed')}</Statistic.Label>
        </Statistic>
        <Statistic color="green">
          <Statistic.Value>{stats.wins}</Statistic.Value>
          <Statistic.Label>{t('teams.stats.wins')}</Statistic.Label>
        </Statistic>
        <Statistic color="red">
          <Statistic.Value>{stats.losses}</Statistic.Value>
          <Statistic.Label>{t('teams.stats.losses')}</Statistic.Label>
        </Statistic>
        <Statistic color="yellow">
          <Statistic.Value>{stats.draws}</Statistic.Value>
          <Statistic.Label>{t('teams.stats.draws')}</Statistic.Label>
        </Statistic>
      </StatisticGroup>

      {/* Win rate + goals */}
      <StatisticGroup widths={3} size="small" style={{ marginBottom: 20 }}>
        <Statistic color="teal">
          <Statistic.Value>{winRate}%</Statistic.Value>
          <Statistic.Label>{t('teams.stats.winRate')}</Statistic.Label>
        </Statistic>
        <Statistic>
          <Statistic.Value>{stats.goalsFor}</Statistic.Value>
          <Statistic.Label>{t('teams.stats.goalsFor')}</Statistic.Label>
        </Statistic>
        <Statistic>
          <Statistic.Value>{stats.goalsAgainst}</Statistic.Value>
          <Statistic.Label>{t('teams.stats.goalsAgainst')}</Statistic.Label>
        </Statistic>
      </StatisticGroup>

      {/* Tournaments */}
      <StatisticGroup widths={2} size="small" style={{ marginBottom: 20 }}>
        <Statistic>
          <Statistic.Value>{stats.tournamentsPlayed}</Statistic.Value>
          <Statistic.Label>{t('teams.stats.tournamentsPlayed')}</Statistic.Label>
        </Statistic>
        <Statistic color="orange">
          <Statistic.Value>{stats.tournamentsWon}</Statistic.Value>
          <Statistic.Label>{t('teams.stats.tournamentsWon')}</Statistic.Label>
        </Statistic>
      </StatisticGroup>

      {/* Streak */}
      <div style={{ marginBottom: 16 }}>
        <strong>{t('teams.stats.streak')}: </strong>
        <Label color={streakColor} size="small">
          {stats.currentStreak}x {stats.streakType}
        </Label>
      </div>

      {/* Last 5 results */}
      {stats.lastFiveResults && stats.lastFiveResults.length > 0 && (
        <div>
          <strong>{t('teams.stats.lastFive')}: </strong>
          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 8 }}>
            {stats.lastFiveResults.map((r, i) => (
              <span
                key={i}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: resultColor(r),
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '0.85em'
                }}
              >
                {r}
              </span>
            ))}
          </span>
        </div>
      )}
    </Segment>
  );
});